import React, { useMemo, useRef, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from 'recharts';
import { MoreVertical, Download, Maximize2, FileSpreadsheet, Loader2 } from 'lucide-react';
import * as htmlToImage from 'html-to-image';

import { Button } from '../../components/ui/button';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from '../../components/ui/dropdown-menu';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../../components/ui/dialog';
import api from '../../lib/api';
import { dedupeUomLabels } from '../../lib/uom';

// Commodity → (denorm field on the series, display UOM, accent color)
const COMMODITY_META = {
  electricity: { field: 'electricity_kwh', uom: 'kWh',     color: '#fbbf24' },
  gas:         { field: 'gas_therms',      uom: 'therms',  color: '#fb923c' },
  water:       { field: 'water_gallons',   uom: 'Gallons', color: '#38bdf8' },
};

// Compact formatter — 1.2K, 3.4M, 1.1B
function compact(n) {
  if (n == null || isNaN(n)) return '0';
  const abs = Math.abs(n);
  if (abs >= 1e9) return `${(n / 1e9).toFixed(1)}B`;
  if (abs >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `${(n / 1e3).toFixed(1)}K`;
  return Math.round(n).toLocaleString();
}

function formatMonthLabel(m) {
  // m = "YYYY-MM" → "Jan '26"
  if (!m || m.length < 7) return m;
  const [y, mo] = m.split('-');
  const d = new Date(Number(y), Number(mo) - 1, 1);
  return d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' }).replace(' ', " '");
}

/**
 * Mini bar-chart that lives directly under each commodity tile.
 * Three-dots menu top-right → Download JPG / View Large / Download CSV.
 */
export function CommodityUsageChart({ commodity, series, accountId, queryParams, title, originalUom }) {
  const meta = COMMODITY_META[commodity];
  const chartRef = useRef(null);
  const [largeOpen, setLargeOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const data = useMemo(
    () => (series || []).map(s => ({ month: s.month, value: Number(s[meta.field] || 0) })),
    [series, meta.field],
  );
  const totalForRange = useMemo(
    () => data.reduce((acc, d) => acc + (d.value || 0), 0),
    [data],
  );

  const handleDownloadJpg = async () => {
    if (!chartRef.current) return;
    try {
      setDownloading(true);
      const dataUrl = await htmlToImage.toJpeg(chartRef.current, {
        backgroundColor: '#ffffff', quality: 0.95, pixelRatio: 2,
      });
      const a = document.createElement('a');
      a.href = dataUrl;
      a.download = `${commodity}-usage-${new Date().toISOString().slice(0, 10)}.jpg`;
      a.click();
    } catch (e) {
      console.error('JPG export failed', e);
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadCsv = async () => {
    try {
      setDownloading(true);
      const res = await api.get('/dashboard/org/mongo/commodity-usage-export', {
        params: { account_id: accountId, commodity, ...queryParams },
      });
      const rows = res.data?.rows || [];
      const header = [
        'Sitename', 'Account', 'Address', 'Vendor',
        'Bill Period From', 'Bill Period To', 'Issue Date',
        'Usage', 'UOM',
      ];
      const csvLines = [header.join(',')];
      for (const r of rows) {
        const row = [
          r.sitename, r.account, r.address, r.vendor,
          r.period_from, r.period_to, r.issue_date,
          r.usage, r.uom,
        ].map((v) => {
          if (v == null) return '';
          const s = String(v);
          return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
        });
        csvLines.push(row.join(','));
      }
      const blob = new Blob([csvLines.join('\n')], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${commodity}-usage-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('CSV export failed', e);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <>
      <div
        className="border-x border-b rounded-b-md bg-white"
        data-testid={`commodity-chart-${commodity}`}
      >
        {/* header strip with the 3-dots menu */}
        <div className="flex items-center justify-between px-3 pt-2 pb-1">
          <div className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
            Monthly usage
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 text-slate-400 hover:text-slate-700"
                data-testid={`commodity-chart-menu-${commodity}`}
                disabled={downloading}
              >
                {downloading
                  ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  : <MoreVertical className="h-3.5 w-3.5" />}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="text-xs">
              <DropdownMenuItem
                onClick={handleDownloadJpg}
                data-testid={`commodity-chart-download-jpg-${commodity}`}
              >
                <Download className="mr-2 h-3.5 w-3.5" /> Download JPG
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => setLargeOpen(true)}
                data-testid={`commodity-chart-view-large-${commodity}`}
              >
                <Maximize2 className="mr-2 h-3.5 w-3.5" /> View large
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={handleDownloadCsv}
                data-testid={`commodity-chart-download-csv-${commodity}`}
              >
                <FileSpreadsheet className="mr-2 h-3.5 w-3.5" /> Download data (CSV)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* as-billed source units caption */}
        {(() => {
          const asBilled = dedupeUomLabels(originalUom);
          return asBilled.length > 0 ? (
            <div
              className="px-3 pb-1 text-[10px] text-slate-400"
              data-testid={`commodity-asbilled-${commodity}`}
            >
              As-billed: {asBilled.slice(0, 3).join(' · ')}{asBilled.length > 3 ? ' · …' : ''}
            </div>
          ) : null;
        })()}

        {/* chart body — captured for JPG export */}
        <div ref={chartRef} className="px-3 pb-3 bg-white">
          <ChartBody data={data} color={meta.color} uom={meta.uom} compact />
        </div>
      </div>

      {/* Large modal */}
      <Dialog open={largeOpen} onOpenChange={setLargeOpen}>
        <DialogContent
          className="max-w-5xl"
          data-testid={`commodity-chart-modal-${commodity}`}
        >
          <DialogHeader>
            <DialogTitle className="capitalize">{title} — monthly usage</DialogTitle>
            <DialogDescription className="text-xs">
              {data.length} month{data.length === 1 ? '' : 's'} ·
              total {compact(totalForRange)} {meta.uom} ·
              date range driven by the dashboard filters
            </DialogDescription>
          </DialogHeader>
          <div className="h-[420px] w-full">
            <ChartBody data={data} color={meta.color} uom={meta.uom} compact={false} />
          </div>
          <div className="mt-2 flex items-center justify-end gap-2">
            <Button variant="outline" size="sm" onClick={handleDownloadJpg}>
              <Download className="mr-2 h-4 w-4" /> JPG
            </Button>
            <Button size="sm" onClick={handleDownloadCsv}>
              <FileSpreadsheet className="mr-2 h-4 w-4" /> CSV
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function ChartBody({ data, color, uom, compact: isCompact }) {
  if (!data || data.length === 0) {
    return (
      <div className="flex h-[110px] items-center justify-center text-xs text-slate-400">
        No usage data in the selected period
      </div>
    );
  }
  return (
    <ResponsiveContainer width="100%" height={isCompact ? 110 : '100%'}>
      <BarChart data={data} margin={{ top: 6, right: 6, bottom: 0, left: -8 }}>
        <CartesianGrid strokeDasharray="2 4" stroke="#e2e8f0" vertical={false} />
        <XAxis
          dataKey="month"
          tick={{ fontSize: 10, fill: '#64748b' }}
          tickFormatter={formatMonthLabel}
          axisLine={false}
          tickLine={false}
          interval={isCompact ? 'preserveStartEnd' : 0}
          angle={isCompact ? 0 : -25}
          height={isCompact ? 16 : 50}
          textAnchor={isCompact ? 'middle' : 'end'}
        />
        <YAxis
          tick={{ fontSize: 10, fill: '#64748b' }}
          tickFormatter={compact}
          axisLine={false}
          tickLine={false}
          width={40}
        />
        <Tooltip
          cursor={{ fill: 'rgba(15,23,42,0.04)' }}
          contentStyle={{
            fontSize: 12, padding: '6px 10px',
            border: '1px solid #e2e8f0', borderRadius: 6,
          }}
          labelFormatter={(m) => formatMonthLabel(m)}
          formatter={(v) => [`${Math.round(v).toLocaleString()} ${uom}`, 'Usage']}
        />
        <Bar dataKey="value" fill={color} radius={[3, 3, 0, 0]} maxBarSize={isCompact ? 18 : 60} />
      </BarChart>
    </ResponsiveContainer>
  );
}
